import {
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { type BlockerFunction, useBlocker } from 'react-router';

const UNLOAD_EVENT = 'beforeunload' as const;

interface UseUnsavedChangesGuardParams {
  isDirty: boolean;
  isSaving: boolean;
  save: () => Promise<boolean>;
  discard: () => void | Promise<void>;
  // A ref (not state) so a caller can flip it and navigate in the same tick —
  // the blocker reads .current at call time, so it sees the flip even though
  // the render that would reflect a state update hasn't happened yet.
  bypassGuardRef?: RefObject<boolean>;
  // Opt-in: tries to keep the work and leave without asking. Resolving false
  // is the only case that surfaces the blocked state for a confirmation.
  leaveSilently?: () => Promise<boolean>;
  onLeft?: () => void;
  // Default true; callers whose content is persisted elsewhere turn it off.
  promptOnUnload?: boolean;
}

interface UseUnsavedChangesGuardResult {
  isBlocked: boolean;
  onSave: () => Promise<void>;
  onDiscard: () => Promise<void>;
  onCancel: () => void;
}

export function useUnsavedChangesGuard({
  isDirty,
  isSaving,
  save,
  discard,
  bypassGuardRef,
  leaveSilently,
  onLeft,
  promptOnUnload = true,
}: UseUnsavedChangesGuardParams): UseUnsavedChangesGuardResult {
  const blocker = useBlocker(
    useCallback<BlockerFunction>(
      ({ currentLocation, nextLocation }) =>
        isDirty &&
        !isSaving &&
        !bypassGuardRef?.current &&
        currentLocation.pathname !== nextLocation.pathname,
      [isDirty, isSaving, bypassGuardRef],
    ),
  );

  const blockerRef = useRef(blocker);
  blockerRef.current = blocker;

  const leaveSilentlyRef = useRef(leaveSilently);
  leaveSilentlyRef.current = leaveSilently;
  const onLeftRef = useRef(onLeft);
  onLeftRef.current = onLeft;
  const [needsConfirmation, setNeedsConfirmation] = useState(false);
  const isBlockedNow = blocker.state === 'blocked';

  useEffect(() => {
    if (!isBlockedNow) {
      setNeedsConfirmation(false);
      return;
    }
    const silentLeave = leaveSilentlyRef.current;
    if (!silentLeave) {
      return;
    }
    let isCurrent = true;
    void silentLeave().then((left) => {
      if (!isCurrent) {
        return;
      }
      if (left) {
        blockerRef.current.proceed?.();
        onLeftRef.current?.();
      } else {
        setNeedsConfirmation(true);
      }
    });
    return () => {
      isCurrent = false;
    };
  }, [isBlockedNow]);

  useEffect(() => {
    if (!isDirty || !promptOnUnload) {
      return undefined;
    }

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };

    window.addEventListener(UNLOAD_EVENT, handleBeforeUnload);
    return () => {
      window.removeEventListener(UNLOAD_EVENT, handleBeforeUnload);
    };
  }, [isDirty, promptOnUnload]);

  const onSave = useCallback(async () => {
    const ok = await save();
    if (ok) {
      blockerRef.current.proceed?.();
    } else {
      blockerRef.current.reset?.();
    }
  }, [save]);

  const onDiscard = useCallback(async () => {
    await discard();
    blockerRef.current.proceed?.();
  }, [discard]);

  const onCancel = useCallback(() => {
    blockerRef.current.reset?.();
  }, []);

  return {
    isBlocked: isBlockedNow && (!leaveSilently || needsConfirmation),
    onSave,
    onDiscard,
    onCancel,
  };
}
